import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Package, Edit2, Check, X, Loader2, Upload, Eye, DollarSign, FileText } from 'lucide-react';
import ImageUploadModal from './ImageUploadModal';
import ImageGalleryModal from './ImageGalleryModal';
import PriceEditModal from './PriceEditModal';
import DescriptionEditModal from './DescriptionEditModal';

function InventoryOverview({ userId }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [loading, setLoading] = useState(false);

  const [editingQtyId, setEditingQtyId] = useState(null);
  const [editingQtyValue, setEditingQtyValue] = useState('');

  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Image modals state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Price modal state
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedProductForPrice, setSelectedProductForPrice] = useState(null);

  // Description modal state
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedProductForDescription, setSelectedProductForDescription] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [userId, search, selectedCat]);

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchInventory() {
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        user_id: userId,
        ...(search && { search }),
        ...(selectedCat && { category_id: selectedCat }),
      };

      const { data } = await axios.get(`${baseUrl}/api/inventory`, { params });
      setItems(data.items);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuantity(inventoryId) {
    const qty = Number(editingQtyValue);
    if (isNaN(qty) || qty < 0) {
      alert('Por favor ingresa un número válido');
      return;
    }

    try {
      await axios.post(`${baseUrl}/api/inventory/update`, {
        user_id: userId,
        inventory_id: inventoryId,
        quantity: qty,
      });

      setItems(items.map(it =>
        it.inventory_id === inventoryId ? { ...it, quantity: qty } : it
      ));
      setEditingQtyId(null);
      setEditingQtyValue('');
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert(error.response?.data?.error || 'Error al actualizar cantidad');
    }
  }

  async function handleSaveTitle(productId) {
    if (!editingTitleValue.trim()) {
      alert('El nombre no puede estar vacío');
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${baseUrl}/api/products/${productId}`,
        { title: editingTitleValue.trim() }
      );

      setItems(items.map(row =>
        row.product_id === productId ? { ...row, title: data.title } : row
      ));
      setEditingTitleId(null);
      setEditingTitleValue('');
    } catch (error) {
      console.error('Error updating title:', error);
      alert(error.response?.data?.error || 'Error al actualizar nombre');
    } finally {
      setSaving(false);
    }
  }

  function startEditingQuantity(item) {
    setEditingQtyId(item.inventory_id);
    setEditingQtyValue(item.quantity.toString());
  }

  function startEditingTitle(item) {
    setEditingTitleId(item.product_id);
    setEditingTitleValue(item.title);
  }

  function cancelEditingQuantity() {
    setEditingQtyId(null);
    setEditingQtyValue('');
  }

  function cancelEditingTitle() {
    setEditingTitleId(null);
    setEditingTitleValue('');
  }

  function openUploadModal(item) {
    setSelectedProduct({
      id: item.product_id,
      title: item.title,
      sku: item.sku
    });
    setUploadModalOpen(true);
  }

  function openGalleryModal(item) {
    setSelectedProduct({
      id: item.product_id,
      title: item.title,
      sku: item.sku
    });
    setGalleryModalOpen(true);
  }

  function openPriceModal(item) {
    setSelectedProductForPrice({
      id: item.product_id,
      title: item.title,
      sku: item.sku,
      price: item.price
    });
    setPriceModalOpen(true);
  }

  function openDescriptionModal(item) {
    setSelectedProductForDescription({
      id: item.product_id,
      title: item.title,
      sku: item.sku,
      description: item.description || ''
    });
    setDescriptionModalOpen(true);
  }

  function handleUploadSuccess() {
    fetchInventory();
  }

  function handleImagesChanged() {
    fetchInventory();
  }

  function handlePriceUpdated(productId, newPrice) {
    setItems(items.map(item =>
      item.product_id === productId ? { ...item, price: newPrice } : item
    ));
    setPriceModalOpen(false);
    setSelectedProductForPrice(null);
  }

  function handleDescriptionUpdated(productId, newDescription) {
    setItems(items.map(item =>
      item.product_id === productId ? { ...item, description: newDescription } : item
    ));
    setDescriptionModalOpen(false);
    setSelectedProductForDescription(null);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <select
                value={selectedCat}
                onChange={(e) => setSelectedCat(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat appearance-none bg-white cursor-pointer"
              >
                <option value="">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
              No se encontraron productos
            </h3>
            <p className="text-sm text-slate-600 font-montserrat">
              {search || selectedCat
                ? 'Intenta ajustar tus filtros de búsqueda'
                : 'Agrega productos a tu inventario para comenzar'}
            </p>
          </div>
        )}

        {/* Desktop Table */}
        {!loading && items.length > 0 && (
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Nombre del Producto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      SKU
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Categoría
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Precio
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Descripción
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Imágenes
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                      Cantidad
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {items.map((item) => (
                    <tr key={item.inventory_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        {editingTitleId === item.product_id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingTitleValue}
                              onChange={(e) => setEditingTitleValue(e.target.value)}
                              className="flex-1 px-3 py-1.5 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm font-montserrat"
                              autoFocus
                              disabled={saving}
                            />
                            <button
                              onClick={() => handleSaveTitle(item.product_id)}
                              disabled={saving}
                              className="p-1.5 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg transition-colors disabled:opacity-50"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={cancelEditingTitle}
                              disabled={saving}
                              className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-900 font-montserrat">{item.title}</span>
                            <button
                              onClick={() => startEditingTitle(item)}
                              className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600 font-montserrat">{item.sku}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600 font-montserrat">
                            {item.categories.join(', ')}
                          </span>
                          <span className="px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold rounded-full font-montserrat">
                            {item.fastmax_count}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm font-semibold text-slate-900 font-montserrat">
                            S/ {parseFloat(item.price).toFixed(2)}
                          </span>
                          <button
                            onClick={() => openPriceModal(item)}
                            className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                            title="Editar precio"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => openDescriptionModal(item)}
                            className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
                            title="Editar descripción"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openUploadModal(item)}
                            className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
                            title="Subir imágenes"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openGalleryModal(item)}
                            className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
                            title="Ver galería"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {editingQtyId === item.inventory_id ? (
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="number"
                              value={editingQtyValue}
                              onChange={(e) => setEditingQtyValue(e.target.value)}
                              className="w-24 px-3 py-1.5 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm text-right font-montserrat"
                              autoFocus
                              min="0"
                            />
                            <button
                              onClick={() => handleSaveQuantity(item.inventory_id)}
                              className="p-1.5 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg transition-colors"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditingQuantity}
                              className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-sm font-semibold text-slate-900 font-montserrat">{item.quantity}</span>
                            <button
                              onClick={() => startEditingQuantity(item)}
                              className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mobile Cards */}
        {!loading && items.length > 0 && (
          <div className="lg:hidden space-y-4">
            {items.map((item) => (
              <div key={item.inventory_id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                {/* Product Name */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Producto</div>
                  {editingTitleId === item.product_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingTitleValue}
                        onChange={(e) => setEditingTitleValue(e.target.value)}
                        className="flex-1 px-3 py-2 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm font-montserrat"
                        autoFocus
                        disabled={saving}
                      />
                      <button
                        onClick={() => handleSaveTitle(item.product_id)}
                        disabled={saving}
                        className="p-2 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={cancelEditingTitle}
                        disabled={saving}
                        className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900 font-montserrat">{item.title}</span>
                      <button
                        onClick={() => startEditingTitle(item)}
                        className="p-1.5 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* SKU */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">SKU</div>
                  <div className="text-sm text-slate-900 font-montserrat">{item.sku}</div>
                </div>

                {/* Category */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Categoría</div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-900 font-montserrat">{item.categories.join(', ')}</span>
                    <span className="px-2 py-0.5 bg-brand-primary/10 text-brand-primary text-xs font-bold rounded-full font-montserrat">
                      {item.fastmax_count}
                    </span>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Precio</div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-900 font-montserrat">
                      S/ {parseFloat(item.price).toFixed(2)}
                    </span>
                    <button
                      onClick={() => openPriceModal(item)}
                      className="p-1.5 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                    >
                      <DollarSign className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Descripción</div>
                  <button
                    onClick={() => openDescriptionModal(item)}
                    className="w-full px-4 py-2 bg-brand-primary/10 text-brand-primary rounded-lg font-semibold font-montserrat hover:bg-brand-primary/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    {item.description ? 'Editar Descripción' : 'Agregar Descripción'}
                  </button>
                </div>

                {/* Images */}
                <div className="mb-3">
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Imágenes</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openUploadModal(item)}
                      className="flex-1 px-4 py-2 bg-brand-primary/10 text-brand-primary rounded-lg font-semibold font-montserrat hover:bg-brand-primary/20 transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Subir
                    </button>
                    <button
                      onClick={() => openGalleryModal(item)}
                      className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <div className="text-xs text-slate-500 font-montserrat mb-1">Cantidad</div>
                  {editingQtyId === item.inventory_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editingQtyValue}
                        onChange={(e) => setEditingQtyValue(e.target.value)}
                        className="flex-1 px-3 py-2 border border-brand-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 text-sm font-montserrat"
                        autoFocus
                        min="0"
                      />
                      <button
                        onClick={() => handleSaveQuantity(item.inventory_id)}
                        className="p-2 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg transition-colors"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEditingQuantity}
                        className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-slate-900 font-montserrat">{item.quantity}</span>
                      <button
                        onClick={() => startEditingQuantity(item)}
                        className="p-1.5 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Upload Modal */}
      {uploadModalOpen && selectedProduct && (
        <ImageUploadModal
          productId={selectedProduct.id}
          productTitle={selectedProduct.title}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedProduct(null);
          }}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Image Gallery Modal */}
      {galleryModalOpen && selectedProduct && (
        <ImageGalleryModal
          productId={selectedProduct.id}
          productTitle={selectedProduct.title}
          onClose={() => {
            setGalleryModalOpen(false);
            setSelectedProduct(null);
          }}
          onImagesChanged={handleImagesChanged}
        />
      )}

      {/* Price Edit Modal */}
      {priceModalOpen && selectedProductForPrice && (
        <PriceEditModal
          productId={selectedProductForPrice.id}
          productTitle={selectedProductForPrice.title}
          currentPrice={selectedProductForPrice.price}
          onClose={() => {
            setPriceModalOpen(false);
            setSelectedProductForPrice(null);
          }}
          onPriceUpdated={handlePriceUpdated}
        />
      )}

      {/* Description Edit Modal */}
      {descriptionModalOpen && selectedProductForDescription && (
        <DescriptionEditModal
          productId={selectedProductForDescription.id}
          productTitle={selectedProductForDescription.title}
          currentDescription={selectedProductForDescription.description}
          onClose={() => {
            setDescriptionModalOpen(false);
            setSelectedProductForDescription(null);
          }}
          onDescriptionUpdated={handleDescriptionUpdated}
        />
      )}
    </div>
  );
}

export default InventoryOverview;